import { TopbarNavigation } from '@components/TopbarNavigation/TopbarNavigation';
import LoadingSpinner from '@components/shared/LoadingSpinner';
import { NotificationsCenterProvider } from '@contexts/NotificationsCenterContext';
import { initMfeDynamicSlot } from '@cw-elements/mfe-dynamic-slot';
import { ErrorBoundary as DatadogErrorBoundary } from '@datadog/browser-rum-react';
import { useBaseUser } from '@hooks/queries/useBaseUser';
import { useOrganization } from '@hooks/queries/useOrganization';
import { useProperty } from '@hooks/queries/useProperty';
import { useAnalyticsIdentity } from '@hooks/useAnalyticsIdentity';
import { ConfirmProvider } from '@hooks/useConfirm';
import utils from '@hooks/useFetchUploadedCropSeason';
import { useLoadRegions } from '@hooks/useLoadRegions';
import { useRebrandRedirect } from '@hooks/useRebrandRedirect';
import { useRefreshOnFieldsMfeEvents } from '@hooks/useRefreshOnFieldsMfeEvents';
import { useS2BImageExclusion } from '@hooks/useS2BImageExclusion';
import {
  selectRightPanelWidth,
  workspaceLayoutStore,
} from '@state/workspaceLayout/workspaceLayout.store';
import { useQueryClient } from '@tanstack/react-query';
import { saveAuthReturnTo } from '@utils/authReturnTo';
import { Layout } from 'antd';
import { Content } from 'antd/lib/layout/layout';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { useBoundStore } from 'src/state/store';
import { SideBar } from '../components';
import ErrorBoundary from '../components/ErrorBoundary';
import ModalContainer from '../components/Modals/ModalContainer';
import { BrowserWarning } from '../components/shared/BrowserWarning';
import { loadCropwiseUser } from '../redux/user/thunks';
import { routePaths } from './routes';

const ErrorFallback = () => <ErrorBoundary />;

export function RootLayout() {
  const isAuthenticated = localStorage.getItem('tokens');
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const ldClient = useLDClient();
  const { property } = useProperty();
  const { organization } = useOrganization();
  const queryClient = useQueryClient();
  const { reset } = useBoundStore((state) => state.regions);
  const {
    fetch: fetchSeasons,
    updateSeasons,
    entities,
    status: fetchSeasonsStatus,
  } = useBoundStore((state) => state.seasons);
  const { user, isLoading } = useBaseUser();
  const navigate = useNavigate();
  const location = useLocation();
  const rightPanelWidth = workspaceLayoutStore(selectRightPanelWidth);

  useS2BImageExclusion(property?.id);
  useAnalyticsIdentity(user);
  useRefreshOnFieldsMfeEvents(property);

  useEffect(() => {
    initMfeDynamicSlot(navigate);
  }, [navigate]);

  useEffect(() => {
    dispatch(loadCropwiseUser());
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      ldClient?.identify({
        kind: 'multi',
        user: {
          key: user.id,
          name: user.name,
          default_workspace: user.default_workspace_id,
        },
      });
    }
  }, [user, ldClient]);

  useRebrandRedirect();

  // I feel really sad doing this
  // Fetch seasons
  useEffect(() => {
    reset();
    if (property) {
      fetchSeasons(property);
    }
  }, [property, reset, fetchSeasons]);

  useLoadRegions(property);

  useEffect(() => {
    const seasons = Object.values(entities);
    if (organization && property && seasons && fetchSeasonsStatus === 'success') {
      // updateSeasons is a stable Zustand action consumed by queryFn, not cache-key data
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryClient.prefetchQuery({
        queryKey: utils.getQueryKey(organization.id, property.id),
        queryFn: () => utils.fetchData(organization.id, property.id, updateSeasons),
        staleTime: 0, // This ensure data will be fetched when changing farms
      });
    }
    // Intentionally fires only when the season fetch flips to success; including
    // entities/property/organization would re-run this prefetch mid-fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSeasonsStatus]);

  if (!isAuthenticated) {
    // Last point that still knows the requested URL: the redirect below replaces it with
    // /sign-in, and OAuth then discards the history.
    saveAuthReturnTo(`${location.pathname}${location.search}`);

    return <Navigate to={routePaths.signIn} replace />;
  }

  if (isLoading || !user) {
    return <LoadingSpinner title={t('hall.loading_user')} className="h-screen" />;
  }

  return (
    <>
      <ModalContainer />
      <ConfirmProvider>
        <Layout>
          <NotificationsCenterProvider>
            <TopbarNavigation />
          </NotificationsCenterProvider>
          <Layout
            className="transition-[padding-right] duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none"
            style={{ paddingRight: rightPanelWidth }}
          >
            <SideBar />
            <Content>
              <DatadogErrorBoundary fallback={ErrorFallback}>{<Outlet />}</DatadogErrorBoundary>
            </Content>
            <BrowserWarning />
          </Layout>
        </Layout>
      </ConfirmProvider>
    </>
  );
}
